import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Droplets, LogOut, RefreshCw, Users, ClipboardList, MessageSquare, Archive, Trash2, RotateCcw, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { key: "customer", label: "Aanvragen", icon: ClipboardList },
  { key: "cleaner", label: "Schoonmakers", icon: Users },
  { key: "contact", label: "Berichten", icon: MessageSquare },
];
const VIEWS = [
  { key: "active", label: "Actief", icon: Inbox },
  { key: "archived", label: "Archief", icon: Archive },
  { key: "deleted", label: "Verwijderd", icon: Trash2 },
];

const daysLeft = (deletedAt) => {
  if (!deletedAt) return 30;
  const diff = 30 - Math.floor((Date.now() - new Date(deletedAt).getTime()) / 86400000);
  return Math.max(diff, 0);
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("mc_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("customer");
  const [view, setView] = useState("active");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (tk, type, vw) => {
    setLoading(true);
    try {
      const headers = { "x-admin-token": tk };
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/leads?type=${type}&view=${vw}`, { headers }),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setAuthed(true);
    } catch {
      toast.error("Onjuiste toegangscode");
      setAuthed(false);
      localStorage.removeItem("mc_admin");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token, tab, view);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (authed) load(token, tab, view);
  }, [tab, view]); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    localStorage.setItem("mc_admin", input);
    setToken(input);
    await load(input, tab, view);
  };

  const logout = () => {
    localStorage.removeItem("mc_admin");
    setToken("");
    setAuthed(false);
    setInput("");
  };

  const act = async (id, action) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { action }, { headers: { "x-admin-token": token } });
      const msg = { archive: "Gearchiveerd", delete: "Verplaatst naar Verwijderd", restore: "Hersteld", unarchive: "Teruggezet" }[action];
      toast.success(msg);
      await load(token, tab, view);
    } catch {
      toast.error("Actie mislukt");
    }
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm space-y-5 rounded-3xl bg-white p-8 shadow-xl">
          <div className="flex items-center gap-2 font-heading text-xl font-bold text-slate-900">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white">
              <Droplets className="h-5 w-5" />
            </span>
            MyCleaner Admin
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Toegangscode</Label>
            <Input data-testid="admin-token-input" type="password" value={input} onChange={(e) => setInput(e.target.value)} className="rounded-xl" placeholder="••••••••" />
          </div>
          <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full rounded-full bg-blue-600 py-6 font-semibold text-white hover:bg-blue-700">
            Inloggen
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-5 py-4 md:px-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white">
              <Droplets className="h-4 w-4" />
            </span>
            MyCleaner Leads
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-refresh-btn" variant="outline" size="sm" onClick={() => load(token, tab, view)} className="rounded-full">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button data-testid="admin-logout-btn" variant="outline" size="sm" onClick={logout} className="rounded-full">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8 md:px-8">
        {stats && (
          <div className="mb-8 grid grid-cols-3 gap-4">
            {[
              { l: "Aanvragen", v: stats.customer },
              { l: "Schoonmakers", v: stats.cleaner },
              { l: "Berichten", v: stats.contact },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <p className="text-sm text-slate-500">{s.l}</p>
                <p className="mt-1 font-heading text-3xl font-bold text-slate-900">{s.v}</p>
              </div>
            ))}
          </div>
        )}

        {/* View switcher */}
        <div className="mb-5 flex flex-wrap gap-2">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const count = v.key === "archived" ? stats?.archived : v.key === "deleted" ? stats?.deleted : null;
            return (
              <button
                key={v.key}
                data-testid={`admin-view-${v.key}`}
                onClick={() => setView(v.key)}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                  view === v.key ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="h-4 w-4" /> {v.label}
                {count > 0 && <span className={`ml-1 rounded-full px-1.5 text-xs ${view === v.key ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Type tabs */}
        <div className="mb-6 flex gap-2">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            return (
              <button
                key={tb.key}
                data-testid={`admin-tab-${tb.key}`}
                onClick={() => setTab(tb.key)}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  tab === tb.key ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="h-4 w-4" /> {tb.label}
              </button>
            );
          })}
        </div>

        {view === "deleted" && (
          <p className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Verwijderde items blijven 30 dagen bewaard en worden daarna definitief verwijderd.
          </p>
        )}

        <div data-testid="admin-leads-list" className="space-y-3">
          {leads.length === 0 && (
            <p className="rounded-2xl bg-white p-8 text-center text-slate-500">Geen items in deze weergave.</p>
          )}
          {leads.map((lead) => (
            <div key={lead.id} data-testid={`lead-card-${lead.id}`} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="font-heading text-lg font-semibold text-slate-900">{lead.data.name}</span>
                <div className="flex items-center gap-3">
                  {view === "deleted" && (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">Nog {daysLeft(lead.deleted_at)} dagen</span>
                  )}
                  <span className="text-xs text-slate-400">{new Date(lead.created_at).toLocaleString("nl-NL")}</span>
                </div>
              </div>
              <div className="grid gap-x-6 gap-y-1 text-sm text-slate-600 sm:grid-cols-2">
                {Object.entries(lead.data)
                  .filter(([k]) => k !== "name")
                  .map(([k, v]) => {
                    const val = Array.isArray(v) ? v.join(", ") : v;
                    return val ? (
                      <p key={k}><span className="font-medium capitalize text-slate-800">{k}:</span> {String(val)}</p>
                    ) : null;
                  })}
              </div>

              {/* Actions */}
              <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                {view === "active" && (
                  <>
                    <Button data-testid={`archive-${lead.id}`} variant="outline" size="sm" onClick={() => act(lead.id, "archive")} className="rounded-full text-slate-600">
                      <Archive className="mr-1.5 h-4 w-4" /> Archiveren
                    </Button>
                    <Button data-testid={`delete-${lead.id}`} variant="outline" size="sm" onClick={() => act(lead.id, "delete")} className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
                      <Trash2 className="mr-1.5 h-4 w-4" /> Verwijderen
                    </Button>
                  </>
                )}
                {view === "archived" && (
                  <>
                    <Button data-testid={`unarchive-${lead.id}`} variant="outline" size="sm" onClick={() => act(lead.id, "unarchive")} className="rounded-full text-slate-600">
                      <RotateCcw className="mr-1.5 h-4 w-4" /> Terugzetten
                    </Button>
                    <Button data-testid={`delete-${lead.id}`} variant="outline" size="sm" onClick={() => act(lead.id, "delete")} className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
                      <Trash2 className="mr-1.5 h-4 w-4" /> Verwijderen
                    </Button>
                  </>
                )}
                {view === "deleted" && (
                  <Button data-testid={`restore-${lead.id}`} variant="outline" size="sm" onClick={() => act(lead.id, "restore")} className="rounded-full text-blue-600">
                    <RotateCcw className="mr-1.5 h-4 w-4" /> Herstellen
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
